export const DESIGN_PRESET_BORDER_STYLES = ['none', 'solid', 'dashed', 'dotted'] as const;
export type DesignPresetBorderStyle = (typeof DESIGN_PRESET_BORDER_STYLES)[number];
export type BorderSideIndex = 0 | 1 | 2 | 3;

export function parseDesignPresetBorderStyles(
	value: string
): [DesignPresetBorderStyle, DesignPresetBorderStyle, DesignPresetBorderStyle, DesignPresetBorderStyle] {
	const parts = value.trim().split(/\s+/);
	if (
		parts.length > 4 ||
		!parts.every((part): part is DesignPresetBorderStyle =>
			DESIGN_PRESET_BORDER_STYLES.some(style => style === part)
		)
	)
		throw new Error('Use none, solid, dashed or dotted for one to four border sides.');
	const top = parts[0];
	const right = parts[1] ?? top;
	return [top, right, parts[2] ?? top, parts[3] ?? right];
}

export function setDesignPresetBorderStyles(
	value: string | undefined,
	sides: readonly BorderSideIndex[],
	style: DesignPresetBorderStyle
): string {
	const next = parseDesignPresetBorderStyles(value ?? 'none');
	for (const side of sides) next[side] = style;
	const [top, right, bottom, left] = next;
	if (top === right && top === bottom && top === left) return top;
	if (top === bottom && right === left) return `${top} ${right}`;
	if (right === left) return `${top} ${right} ${bottom}`;
	return next.join(' ');
}

/*
 * The per-side border model the Inspector and design presets share. A side with width 0 is not drawn, so
 * `border-width` carries which sides show (a CSS shorthand of up to four values) and `border-style` holds one line
 * style. Older values put `none` into `border-style` per side; reading maps such a side to width 0.
 */

export const DESIGN_PRESET_LINE_STYLES = ['solid', 'dashed', 'dotted'] as const;
export type DesignPresetLineStyle = (typeof DESIGN_PRESET_LINE_STYLES)[number];

const isLineStyle = (value: string): value is DesignPresetLineStyle =>
	(DESIGN_PRESET_LINE_STYLES as readonly string[]).includes(value);

const words = (value: unknown) =>
	(value === undefined || value === null ? '' : String(value)).trim().split(/\s+/).filter(Boolean);

/** The four sides a CSS shorthand names, in top right bottom left order */
export function expandCssSides(value: unknown): [string, string, string, string] {
	const [a, b = a, c = a, d = b] = words(value);
	if (a === undefined) return ['0', '0', '0', '0'];
	return [a, b, c, d];
}

/** The shortest shorthand for four sides: one value when they agree, else all four */
export function collapseCssSides(sides: readonly string[]): string {
	const [top, right, bottom, left] = sides;
	if (top === right && top === bottom && top === left) return top;
	return `${top} ${right} ${bottom} ${left}`;
}

/** The line style a border draws with; `none` sides, legacy values and no value fall back to solid */
export function getBorderLineStyle(borderStyle: unknown): DesignPresetLineStyle {
	return words(borderStyle).find(isLineStyle) ?? 'solid';
}

/** Whether a border shows at all: no style, or every side `none`, means off */
export function hasBorder(borderStyle: unknown): boolean {
	return words(borderStyle).some(isLineStyle);
}

/** Whether `border-style` still names sides, as older values did */
export function isPerSideBorderStyle(borderStyle: unknown): boolean {
	return words(borderStyle).length > 1;
}

/** Each side's width, with a side styled `none` read as 0 */
export function getBorderSideWidths(width: unknown, style: unknown): [string, string, string, string] {
	const widths = expandCssSides(width);
	const styles = expandCssSides(style === undefined ? undefined : style);
	return widths.map((side, index) => (style !== undefined && styles[index] === 'none' ? '0' : side)) as [
		string,
		string,
		string,
		string,
	];
}

const SIDE_REFERENCE = /^var\(--sc-token-[a-z][a-z0-9-]*\)$/;

/** A preset border width: one to four sides, each a px length, 0, or a token reference */
export function normalizeDesignPresetBorderWidth(value: unknown): string {
	if (typeof value !== 'string' || !value.trim() || /[;{}<>]/.test(value))
		throw new Error('Border width requires one to four px values.');
	const parts = words(value);
	if (parts.length > 4) throw new Error('Border width takes at most four sides.');
	const sides = parts.map(part => {
		if (SIDE_REFERENCE.test(part)) return part;
		if (/^0+(\.0+)?(px)?$/i.test(part)) return '0';
		if (!/^(?:\d+(?:\.\d+)?|\.\d+)px$/i.test(part)) throw new Error('Border width sides require px values.');
		return `${Number(part.slice(0, -2))}px`;
	});
	return collapseCssSides(expandCssSides(sides.join(' ')));
}
